import React from "react"
import { Baby, Plus } from "lucide-react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { formatDateOfBirth } from "./utils"
import { ChildDropdownItem } from "./types"
import { useTheme } from "@/lib/theme/ThemeContext"

interface CompactSelectorProps {
  selectedValue: string
  handleChange: (childId: string) => void
  loading: boolean
  children: ChildDropdownItem[]
  selectClassName?: string
  loadingError: string | null
  showAddButton: boolean
  onAddChild?: () => void
}

export function CompactSelector({
  selectedValue,
  handleChange,
  loading,
  children,
  selectClassName,
  loadingError,
  showAddButton,
  onAddChild,
}: CompactSelectorProps) {
  const isLightTheme = false;
  
  return (
    <div className="relative">
      <Select
        value={selectedValue || "placeholder"} 
        onValueChange={handleChange}
        disabled={loading || children.length === 0}
      >
        <SelectTrigger
          className={cn(
            isLightTheme 
              ? "bg-purple-100/70 border-purple-200 text-purple-900 hover:bg-purple-200/50"
              : "bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20 text-purple-100 hover:from-purple-500/20 hover:to-blue-500/20",
            "rounded-xl transition-all duration-300 focus:ring-2",
            isLightTheme ? "focus:ring-purple-300" : "focus:ring-purple-500/30",
            selectClassName
          )}
        >
          <div className="flex items-center gap-2">
            <div className={cn(
              "p-1.5 rounded-lg",
              isLightTheme 
                ? "bg-purple-200 text-purple-700"
                : "bg-gradient-to-br from-purple-500/20 to-blue-500/20 text-purple-400"
            )}>
              <Baby className="w-5 h-5" />
            </div>
            <SelectValue
              placeholder={loading ? "Loading children..." : "Select a child"}
              className={isLightTheme ? "text-purple-900" : "text-purple-300"}
            />
          </div>
        </SelectTrigger>
        <SelectContent
          className={cn(
            isLightTheme 
              ? "bg-white/90 backdrop-blur-sm border border-purple-200 shadow-lg"
              : "bg-gradient-to-br from-purple-900/90 to-blue-900/90 border border-purple-500/20 backdrop-blur-xl shadow-xl shadow-purple-500/10",
            "rounded-xl w-full min-w-[250px]"
          )}
        >
          {children.length > 0 ? (
            children.map((child) => (
              <SelectItem
                key={child.id}
                value={child.id || "unavailable"} 
                className={cn(
                  "cursor-pointer transition-colors duration-200",
                  isLightTheme 
                    ? "hover:bg-purple-100 focus:bg-purple-100 text-slate-700 py-3"
                    : "hover:bg-purple-500/20 focus:bg-purple-500/20 text-purple-100 py-3"
                )}
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium">{child.name}</span>
                  {child.date_of_birth && (
                    <span className={`text-xs ml-1 ${
                      isLightTheme ? "text-slate-500" : "text-purple-400"
                    }`}>
                      {formatDateOfBirth(child.date_of_birth)}
                    </span>
                  )}
                </div>
              </SelectItem>
            ))
          ) : (
            <div className={`py-3 px-2 text-center ${
              isLightTheme ? "text-slate-500" : "text-purple-300"
            }`}>
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className={`animate-spin rounded-full h-4 w-4 border-b-2 mr-2 ${
                    isLightTheme ? "border-purple-400" : "border-purple-400"
                  }`}></div>
                  <span>Loading children...</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <span>No children found</span>
                  {showAddButton && onAddChild && (
                    <Button
                      onClick={onAddChild}
                      variant="link"
                      className={isLightTheme ? "text-purple-600 hover:text-purple-700" : "text-purple-400 hover:text-purple-300"}
                    >
                      Add your first child
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </SelectContent>
      </Select>
      {loadingError && (
        <p className="text-red-400 text-xs mt-1">{loadingError}</p>
      )}
    </div>
  )
}
